import { useMutation } from '@apollo/client'
import { format, formatDistanceToNow } from 'date-fns'
import { Loader, MessageSquareText, Trash2 } from 'lucide-react'
import { useState, useTransition } from 'react'
import { toast } from 'sonner'
import {
  ADD_MIGRATION_NOTE_MUTATION,
  COMPANY_MIGRATION_SCHEDULE_QUERY,
  DELETE_MIGRATION_NOTE_MUTATION,
} from '@/components/layout/oci-migration-notice/queries'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  useAuthenticatedIdUser,
  useMixpanel,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  AddClusterMigrationScheduleNoteMutation,
  AddClusterMigrationScheduleNoteMutationVariables,
  CompanyClusterMigrationScheduleQuery,
  CompanyClusterMigrationScheduleQueryVariables,
  DeleteClusterMigrationScheduleNoteMutation,
  DeleteClusterMigrationScheduleNoteMutationVariables,
} from '@/components/layout/oci-migration-notice/queries.types.gen'

type Note = NonNullable<
  CompanyClusterMigrationScheduleQuery['companyClusterMigrationSchedule']
>['notes'][number]

const NOTE_MAX_LENGTH = 2000

const MAX_NOTES = 50

type NoteItemProps = {
  idCompany: string
  note: Note
  isOwn: boolean
  canDelete: boolean
}

const NoteItem = ({ idCompany, note, isOwn, canDelete }: NoteItemProps) => {
  const [isDeleting, startDeleting] = useTransition()

  const [deleteNote] = useMutation<
    DeleteClusterMigrationScheduleNoteMutation,
    DeleteClusterMigrationScheduleNoteMutationVariables
  >(DELETE_MIGRATION_NOTE_MUTATION, {
    refetchQueries: [COMPANY_MIGRATION_SCHEDULE_QUERY],
    awaitRefetchQueries: true,
  })

  const handleDelete = () => {
    startDeleting(async () => {
      try {
        await deleteNote({ variables: { idCompany, id: note.id } })
        toast(<ToasterContent variant="success">Note deleted</ToasterContent>)
      } catch {
        toast(
          <ToasterContent variant="error">
            We could not delete this note. Please try again.
          </ToasterContent>
        )
      }
    })
  }

  return (
    <li
      className={cn(
        'flex flex-col gap-1 rounded-md bg-muted/60 px-3 py-2 transition-opacity',
        isDeleting && 'opacity-60'
      )}
    >
      <p className="whitespace-pre-line break-words text-sm">{note.content}</p>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {isOwn ? 'You' : 'A teammate'} ·{' '}
          <time
            dateTime={note.createdAt}
            title={format(new Date(note.createdAt), 'PPpp')}
          >
            {formatDistanceToNow(new Date(note.createdAt), {
              addSuffix: true,
            })}
          </time>
        </span>
        {isOwn && canDelete ? (
          <Button
            variant="ghost"
            size="icon-sm"
            className="-my-1 -mr-1.5 text-muted-foreground"
            aria-label="Delete note"
            disabled={isDeleting}
            onClick={handleDelete}
          >
            {isDeleting ? <Loader className="animate-spin" /> : <Trash2 />}
          </Button>
        ) : null}
      </div>
    </li>
  )
}

type ScheduleNotesProps = {
  idCompany: string
  waveLabel: string
}

export const ScheduleNotes = ({ idCompany, waveLabel }: ScheduleNotesProps) => {
  const { rbac } = useRBAC()
  const idUser = useAuthenticatedIdUser()
  const { track } = useMixpanel()
  const [content, setContent] = useState('')
  const [isPending, startTransition] = useTransition()

  const [addNote] = useMutation<
    AddClusterMigrationScheduleNoteMutation,
    AddClusterMigrationScheduleNoteMutationVariables
  >(ADD_MIGRATION_NOTE_MUTATION, {
    refetchQueries: [COMPANY_MIGRATION_SCHEDULE_QUERY],
    awaitRefetchQueries: true,
  })

  const { data } = useSuspenseQuery<
    CompanyClusterMigrationScheduleQuery,
    CompanyClusterMigrationScheduleQueryVariables
  >(COMPANY_MIGRATION_SCHEDULE_QUERY, { variables: { idCompany } })

  const schedule = data?.companyClusterMigrationSchedule

  if (!schedule || schedule.status === 'cancelled') {
    return null
  }

  const { notes } = schedule
  const canEdit = rbac.company(idCompany).apps.update.can()
  const trimmedContent = content.trim()
  const hasReachedLimit = notes.length >= MAX_NOTES

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (!trimmedContent) {
      return
    }

    startTransition(async () => {
      try {
        await addNote({ variables: { idCompany, content: trimmedContent } })
        track('OCI migration note added', { wave: waveLabel })
        setContent('')
      } catch {
        toast(
          <ToasterContent variant="error">
            We could not add your note. Please try again.
          </ToasterContent>
        )
      }
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-2 font-medium">
          <MessageSquareText
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground"
          />
          Notes for our team
        </span>
        <Muted as="p" className="text-xs">
          Tell us anything about your setup or the timing. Your team and the
          Sevalla migration team can read them.
        </Muted>
      </div>
      {notes.length ? (
        <ul className="flex flex-col gap-2">
          {notes.map((note) => (
            <NoteItem
              key={note.id}
              idCompany={idCompany}
              note={note}
              isOwn={note.idUser === idUser}
              canDelete={canEdit}
            />
          ))}
        </ul>
      ) : null}
      {canEdit ? (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <Label htmlFor="migration-note" className="sr-only">
            New note
          </Label>
          <Textarea
            id="migration-note"
            value={content}
            maxLength={NOTE_MAX_LENGTH}
            disabled={hasReachedLimit}
            placeholder="For example, a busy period to avoid or a service that needs extra care"
            onChange={(event) => setContent(event.target.value)}
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {hasReachedLimit
                ? `You can keep up to ${MAX_NOTES} notes. Delete one to add another.`
                : `${content.length} / ${NOTE_MAX_LENGTH}`}
            </span>
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={!trimmedContent || isPending || hasReachedLimit}
            >
              {isPending ? <Loader className="animate-spin" /> : null}
              Add note
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  )
}
